import * as amplitude from "@amplitude/analytics-browser";
import { Button, Flex, Skeleton } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { RiEditLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { SearchAndFilterTypes } from "src/constants";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  CapacityConfig,
  GetCapacitiesBySchoolAndEnrollmentPeriodResponse,
} from "src/generated/hooks/form-service/requests";
import { Selection } from "src/hooks/useMultiselectState";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { isNonEmptyString } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { BirdsEyeViewTable } from "./BirdsEyeViewTable";
import { CapacitiesEdit } from "./CapacitiesEdit";
import { CapacityTable, Mode, TableLoading } from "./CapacityTable";
import { Capacity } from "./Formik";
import { AllSchoolsKey, SchoolSearchSelect } from "./SchoolSearchSelect";

type Props = {
  enrollmentPeriodId: string;
  hideAllSchoolsView?: boolean;
  configOverride?: CapacityConfig;
  hideEditButton?: boolean;
};
type SelectedSchool =
  | { kind: "no-school" }
  | { kind: "all-schools" }
  | { kind: "one-school"; schoolId: string };

export const Capacities: React.FC<Props> = ({
  enrollmentPeriodId,
  hideAllSchoolsView = false,
  configOverride,
  hideEditButton = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const schoolId = searchParams.get(SearchAndFilterTypes.School) ?? "";

  const selectedSchool: SelectedSchool = React.useMemo((): SelectedSchool => {
    if (!isNonEmptyString(schoolId)) {
      return { kind: "no-school" };
    }

    if (schoolId === AllSchoolsKey) {
      return { kind: "all-schools" };
    }

    return { kind: "one-school", schoolId };
  }, [schoolId]);

  const query = FSQ.useGetCapacitiesBySchoolAndEnrollmentPeriod(
    {
      path: {
        schoolId,
        enrollmentPeriodId,
      },
    },
    [schoolId, enrollmentPeriodId],
    { enabled: selectedSchool.kind === "one-school" }
  );

  const capacityRD: RD.RemoteData<
    unknown,
    { capacities: Capacity[]; config: CapacityConfig }
  > = useMemo(() => RD.fromRestResult(query).map(toCapacities), [query]);

  const [selection, onSelectionChange] = React.useState(
    Selection.create((row: Capacity) => row.id)
  );

  const mode: Mode = searchParams.get("mode") === "edit" ? "edit" : "view";
  const setMode = React.useCallback(
    (mode: Mode) => {
      searchParams.set("mode", mode);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const userPermissions = useUserPermissions();
  const isAllowedToEditCapacity = userPermissions.hasOne("school.grade:update");

  React.useEffect(() => {
    return () => {
      // Reset mode to view when component is unmount.
      // NOTE: Use window.history.replaceState instead of setSearchParams because
      // of race condition with TabsWithHash also updating the URL.
      // window.history.replaceState doesn't have that race condition issue.
      const url = new URL(window.location.href);
      if (url.searchParams.has("mode")) {
        url.searchParams.delete("mode");
        window.history.replaceState(window.history.state, "", url.toString());
      }
    };
  }, []);

  if (!hideEditButton && mode === "edit") {
    return (
      <RemoteDataView
        remoteData={capacityRD}
        error={() => <GenericError />}
        loading={<TableLoading />}
      >
        {(data) => (
          <CapacitiesEdit
            enrollmentPeriodId={enrollmentPeriodId}
            schoolId={schoolId}
            data={data}
            closeEdit={() => setMode("view")}
          />
        )}
      </RemoteDataView>
    );
  }

  return (
    <Flex direction="column" gap="4">
      <SchoolSearchSelect
        hideAllSchools={hideAllSchoolsView}
        enrollmentPeriodId={enrollmentPeriodId}
        schoolId={schoolId}
        onChange={async (item: Item | null) => {
          if (item === null) {
            searchParams.delete(SearchAndFilterTypes.School);
          } else {
            searchParams.set(SearchAndFilterTypes.School, item.key);
          }
          onSelectionChange(selection.clear());
          setSearchParams(searchParams);
        }}
      />

      {selectedSchool.kind === "one-school" ? (
        <CapacityTable
          remoteData={capacityRD}
          schoolId={schoolId}
          enrollmentPeriodId={enrollmentPeriodId}
          mode={mode}
          configOverrride={configOverride}
          additionalNodes={
            userPermissions.isLoading ? (
              <Skeleton width="5rem" height="2rem" />
            ) : isAllowedToEditCapacity &&
              !hideEditButton &&
              isNonEmptyString(schoolId) ? (
              <Button
                marginLeft="2"
                size="sm"
                leftIcon={<RiEditLine />}
                onClick={async () => {
                  amplitude.track("Clicked edit");
                  setMode("edit");
                }}
              >
                Edit
              </Button>
            ) : null
          }
        />
      ) : null}
      {!hideAllSchoolsView && selectedSchool.kind !== "one-school" ? (
        <BirdsEyeViewTable enrollmentPeriodId={enrollmentPeriodId} />
      ) : null}
    </Flex>
  );
};

function toCapacities(data: GetCapacitiesBySchoolAndEnrollmentPeriodResponse): {
  config: CapacityConfig;
  capacities: Capacity[];
} {
  return {
    config: data.data.config,
    capacities: data.data.capacities.map(
      (row) =>
        ({
          id: row.id,
          name: row.gradeConfig.label,
          totalCapacity: row.totalCapacity,
          accepted: row.accepted,
          offered: row.offered,
          waitlisted: row.waitlisted,
          seatsAvailable: row.seatsAvailable,
          additionalSeatsTaken1: row.additionalSeatsTaken1,
          additionalSeatsTaken2: row.additionalSeatsTaken2,
          programs: row.programs.map(
            (programRow) =>
              ({
                id: programRow.id,
                name: programRow.program?.label ?? "",
                totalCapacity: programRow.totalCapacity,
                offered: programRow.offered,
                accepted: programRow.accepted,
                waitlisted: programRow.waitlisted,
                seatsAvailable: programRow.seatsAvailable,
                additionalSeatsTaken1: programRow.additionalSeatsTaken1,
                additionalSeatsTaken2: programRow.additionalSeatsTaken2,
              } satisfies Capacity)
          ),
        } satisfies Capacity)
    ),
  };
}
